import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import useFetch from './useFetch';
import { domain } from './Domain';
import { motion } from 'framer-motion';
import '../css/Team.css';

const TeamSkeleton = () => (
    <div className="row g-4 g-lg-5">
        {[0, 1, 2].map((i) => (
            <div key={i} className="col-12 col-sm-6 col-xl-4">
                <div className="team-skeleton-card">
                    <div className="team-skeleton-photo" />
                    <div className="team-skeleton-body">
                        <div className="team-skeleton-line team-skeleton-line--title" />
                        <div className="team-skeleton-line team-skeleton-line--short" />
                        <div className="team-skeleton-line" />
                        <div className="team-skeleton-line" />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

const Team = () => {
    const { data, loading, error } = useFetch(`${domain}/team-members`);
    const members = Array.isArray(data) ? data : [];
    const hasMembers = !loading && !error && members.length > 0;

    return (
        <div className="team-page">
            <Header />
            <main className="team-main">
                <section className="team-hero">
                    <div className="container">
                        <motion.div
                            className="team-hero-inner"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45 }}
                        >
                            <p className="team-kicker">Meet the team</p>
                            <h1 className="team-title">
                                The people behind your quotes &amp; support
                            </h1>
                            <p className="team-lead">
                                When you request pricing, technical help, or shipment
                                updates, you work with this group—reachable by email,
                                phone, or LinkedIn.
                            </p>
                        </motion.div>
                    </div>
                </section>

                <div className="team-ribbon" role="presentation">
                    <div className="container">
                        <p className="team-ribbon-text">
                            Quotes · Technical support · Logistics · Sourcing
                        </p>
                    </div>
                </div>

                <section className="team-content">
                    <div className="container">
                        {error && (
                            <div className="team-alert" role="alert">
                                {error}
                            </div>
                        )}

                        {loading && <TeamSkeleton />}

                        {!loading &&
                            !error &&
                            members.length === 0 && (
                                <motion.div
                                    className="team-empty"
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <p className="team-empty-title">
                                        Profiles coming soon
                                    </p>
                                    <p className="team-empty-text">
                                        Team members can be added in the Django admin.
                                        In the meantime, we&apos;re still here to help— reach out directly.
                                    </p>
                                    <Link
                                        to="/contact-us"
                                        className="team-empty-cta"
                                    >
                                        Contact us
                                    </Link>
                                </motion.div>
                            )}

                        {hasMembers && (
                            <div className="row g-4 g-lg-5">
                                {members.map((member, index) => (
                                    <motion.div
                                        key={member.id}
                                        className="col-12 col-sm-6 col-xl-4"
                                        initial={{ opacity: 0, y: 24 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{
                                            once: true,
                                            margin: '-40px',
                                        }}
                                        transition={{
                                            duration: 0.4,
                                            delay: Math.min(index * 0.06, 0.3),
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                    >
                                        <article className="team-member-card">
                                            <div className="team-member-photo-wrap">
                                                {member.photo_url ? (
                                                    <img
                                                        src={member.photo_url}
                                                        alt={member.full_name}
                                                        className="team-member-photo"
                                                    />
                                                ) : (
                                                    <div
                                                        className="team-member-placeholder"
                                                        aria-hidden
                                                    >
                                                        {member.full_name
                                                            .charAt(0)
                                                            .toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="team-member-body">
                                                <h2 className="team-member-name">
                                                    {member.full_name}
                                                </h2>
                                                {member.role && (
                                                    <p className="team-member-role">
                                                        {member.role}
                                                    </p>
                                                )}
                                                {member.bio && (
                                                    <p className="team-member-bio">
                                                        {member.bio}
                                                    </p>
                                                )}
                                                <div className="team-member-actions">
                                                    {member.email && (
                                                        <a
                                                            href={`mailto:${member.email}`}
                                                            className="team-member-icon-link"
                                                            title={`Email ${member.full_name}`}
                                                        >
                                                            <i
                                                                className="fa-solid fa-envelope"
                                                                aria-hidden
                                                            />
                                                            <span className="visually-hidden">
                                                                Email{' '}
                                                                {member.full_name}
                                                            </span>
                                                        </a>
                                                    )}
                                                    {member.phone && (
                                                        <a
                                                            href={`tel:${member.phone.replace(/\s/g, '')}`}
                                                            className="team-member-icon-link"
                                                            title="Phone"
                                                        >
                                                            <i
                                                                className="fa-solid fa-phone"
                                                                aria-hidden
                                                            />
                                                            <span className="visually-hidden">
                                                                Call{' '}
                                                                {member.full_name}
                                                            </span>
                                                        </a>
                                                    )}
                                                    {member.linkedin_url && (
                                                        <a
                                                            href={
                                                                member.linkedin_url
                                                            }
                                                            className="team-member-icon-link"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="LinkedIn"
                                                        >
                                                            <i
                                                                className="fa-brands fa-linkedin-in"
                                                                aria-hidden
                                                            />
                                                            <span className="visually-hidden">
                                                                LinkedIn profile for{' '}
                                                                {member.full_name}
                                                            </span>
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </article>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default Team;
